import Image from '@atoms/Image/Image';
import { ILinkedImage } from '@models/ILinkedImage';
import PaginationCarousel from '@molecules/PaginationCarousel/PaginationCarousel';
import updateDataLayer from '@utils/helpers/gtm';
import slugify from '@utils/helpers/slugify';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props {
  readonly linkedImages: ILinkedImage[];
  readonly useLightVersionCompact?: boolean;
}

const CompactAwardsCarousel = ({
  linkedImages,
  useLightVersionCompact,
}: Props) => {
  const [hoverItemIndex, setHoverItemIndex] = useState<number | null>(null);

  const isTwoOrFourImages =
    linkedImages.length === 2 || linkedImages.length === 4;

  return (
    <motion.div
      className="overflow-swiper xl:col-span-10 xl:col-start-2"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
      data-testid="CT-77-Awards-Carousel-Compact"
    >
      <PaginationCarousel
        id="Compact-Awards-Carousel"
        paginationClasses={classNames(
          'mt-7.5 md:mt-10 md:hidden',
          linkedImages.length <= 4 ? 'hidden' : ''
        )}
        swiperClasses={
          isTwoOrFourImages
            ? '[&>.swiper-wrapper]:justify-center'
            : 'md:[&>.swiper-wrapper]:justify-center'
        }
        spaceBetween={-20}
        slidesPerView={3.8}
        initialSlide={
          isTwoOrFourImages ? 0 : Math.ceil(linkedImages.length / 2) - 1
        }
        centeredSlides={!isTwoOrFourImages}
        breakpoints={{
          768: {
            slidesPerView: 8,
            spaceBetween: -10,
            centeredSlides: false,
          },
          1024: {
            spaceBetween: -150,
            slidesPerView: 8,
            centeredSlides: false,
          },
          1440: {
            slidesPerView: 8,
            spaceBetween: -10,
            centeredSlides: false,
          },
          1920: {
            slidesPerView: 8,
            spaceBetween: -200,
            centeredSlides: false,
          },
        }}
      >
        {linkedImages
          .filter((linkedImage) => !!linkedImage)
          .map((linkedImage, index) => (
            <SwiperSlide
              key={slugify(
                linkedImage.image.desktop?.url
                  ? `${linkedImage.image.desktop.url}${index}`
                  : `${index}`
              )}
              className={classNames('inline-flex', {
                'z-20': hoverItemIndex === index,
                'z-10': hoverItemIndex !== index,
              })}
            >
              <a
                className="inline-flex"
                href={linkedImage.link.url}
                target={linkedImage.link.target}
                onClick={() =>
                  updateDataLayer({
                    event: 'awardsIcons',
                    link_url: linkedImage.link.url,
                  })
                }
                data-testid={`CT-77-Awards-Carousel-Item-Compact-Item-${index}`}
              >
                <div
                  className={classNames(
                    'flex h-[93px] w-[93px] items-center overflow-hidden rounded-full border-2 p-2 align-middle xl:h-[160px] xl:w-[160px] xl:p-4',
                    useLightVersionCompact
                      ? {
                          'border-white bg-black': hoverItemIndex === index,
                          'border-grey bg-white': hoverItemIndex !== index,
                        }
                      : {
                          'border-yellow bg-yellow': hoverItemIndex === index,
                          'border-white bg-black-100': hoverItemIndex !== index,
                        }
                  )}
                  onMouseOver={() => setHoverItemIndex(index)}
                  onMouseOut={() => setHoverItemIndex(null)}
                  onFocus={() => setHoverItemIndex(index)}
                  onBlur={() => setHoverItemIndex(null)}
                >
                  <div
                    className={classNames(
                      'small-text visible w-full text-center',
                      useLightVersionCompact
                        ? '!font-medium bg-black text-white'
                        : 'bg-yellow text-black-100',
                      {
                        hidden: hoverItemIndex !== index,
                      }
                    )}
                    data-testid={`CT-77-Awards-Carousel-Item-Compact-Item-${index}-Link-Name`}
                  >
                    {linkedImage.link.name}
                  </div>
                  <Image
                    key={linkedImage.image?.desktop?.url}
                    classes={classNames('visible', {
                      hidden: hoverItemIndex === index,
                    })}
                    image={linkedImage.image}
                    imageClasses="select-none w-full h-full object-contain min-h-[160px]"
                    dataTestId={`CT-77-Awards-Carousel-Item-Compact-Item-${index}-Image`}
                  />
                </div>
              </a>
            </SwiperSlide>
          ))}
      </PaginationCarousel>
    </motion.div>
  );
};

export default CompactAwardsCarousel;
